import { useMemo } from 'react';
import { useConversations } from 'src/graphql/entities/conversation';

import { ConditionInput, SortingInput } from 'src/graphql/types';

import useDebouncedValue from 'src/utils/hooks/use-debounced-value';

const DEFAULT_FILTER: ConditionInput[] = [];

const DEFAULT_TARGET_IDS = [
  'conversation/conversationId',
  'client/conversationParticipant/requesterForConversations/conversationId',
  'clientPhoneNumber/client/addresses/associatedConversations/conversationId',
  'clientEmail/client/addresses/associatedConversations/conversationId',
  'address/associatedConversations/conversationId',
  'client/conversationParticipant/conversationChannels/participations/conversationId',
  'platformUser/conversationParticipant/conversationChannels/participations/conversationId',
];

export const useSearchConversations = (params: {
  searchTerm: string | null;
  targetIdAttributes?: string[];
  filter?: ConditionInput[];
  limit?: number;
  sort?: SortingInput | SortingInput[];
  skip?: boolean;
  showResultsWhenEmptySearchTerm?: boolean;
  dynamicAttributePaths?: string[];
  includeDeleted?: boolean;
}) => {
  // instead of debouncing search calls it debounces changes of the searchTerm
  // it allows to be more flexible in terms of filters handling
  const searchTerm = useDebouncedValue(params.searchTerm, 500);

  const {
    filter = DEFAULT_FILTER,
    skip,
    sort,
    showResultsWhenEmptySearchTerm,
    targetIdAttributes,
    limit,
    dynamicAttributePaths,
    includeDeleted,
  } = params;

  const queryOptions = useMemo(() => {
    const searchParams = searchTerm
      ? {
          searchTerm,
          targetIdAttributes: targetIdAttributes ?? DEFAULT_TARGET_IDS,
        }
      : undefined;

    return {
      filter,
      sort,
      limit,
      offset: 0,
      searchParams,
      dynamicAttributePaths,
      skip: skip || (!showResultsWhenEmptySearchTerm && !searchTerm),
      includeDeleted,
    };
  }, [
    searchTerm,
    targetIdAttributes,
    filter,
    sort,
    limit,
    dynamicAttributePaths,
    skip,
    showResultsWhenEmptySearchTerm,
    includeDeleted,
  ]);

  const { conversations, loading, hasMore, ...rest } = useConversations(queryOptions);

  return {
    ...rest,
    isLoading: loading,
    records: conversations,
    hasMore,
  };
};
